import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { store, updateTokens, logout } from '../store';

const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = store.getState().auth.accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: any) => void; reject: (e: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status !== 401 || originalRequest._retry) return Promise.reject(error);

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = store.getState().auth.refreshToken;
    if (!refreshToken) { store.dispatch(logout()); return Promise.reject(error); }

    try {
      const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${refreshToken}` },
      });
      store.dispatch(updateTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken }));
      processQueue(null, data.accessToken);
      originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      store.dispatch(logout());
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export const api = {
  // Auth
  requestSignInOtp: (email: string) => apiClient.post('/auth/sign-in/request-otp', { email }),
  verifySignInOtp: (email: string, otp: string) => apiClient.post('/auth/sign-in/verify-otp', { email, otp }),
  initiateSignUp: (data: { name: string; email: string; password: string }) => apiClient.post('/auth/sign-up/initiate', data),
  verifySignUpOtp: (email: string, otp: string) => apiClient.post('/auth/sign-up/verify-otp', { email, otp }),
  logout: () => apiClient.post('/auth/logout'),

  // User (self)
  getMe: () => apiClient.get('/users/me'),
  updateMe: (data: any) => apiClient.patch('/users/me', data),

  // Public
  getSnapshot: (username: string) => apiClient.get(`/public/snapshot/${username}`),
  getPublicPortfolios: () => apiClient.get('/public/portfolios'),

  // Theme & config
  getTheme: () => apiClient.get('/theme'),
  getThemeByUsername: (username: string) => apiClient.get(`/theme/${username}`),
  getSiteConfig: () => apiClient.get('/site-config'),
  getSiteConfigByUsername: (username: string) => apiClient.get(`/site-config/${username}`),

  // Sections
  getHero: () => apiClient.get('/sections/hero'),
  updateHero: (id: string, data: any) => apiClient.patch(`/sections/hero/${id}`, data),
  createHero: (data: any) => apiClient.post('/sections/hero', data),

  getSocialLinks: () => apiClient.get('/sections/social-links'),
  createSocialLink: (data: any) => apiClient.post('/sections/social-links', data),
  updateSocialLink: (id: string, data: any) => apiClient.patch(`/sections/social-links/${id}`, data),
  deleteSocialLink: (id: string) => apiClient.delete(`/sections/social-links/${id}`),

  getSpecialty: () => apiClient.get('/sections/specialty'),
  updateSpecialty: (id: string, data: any) => apiClient.patch(`/sections/specialty/${id}`, data),
  createSpecialty: (data: any) => apiClient.post('/sections/specialty', data),

  getEducation: () => apiClient.get('/sections/education'),
  createEducation: (data: any) => apiClient.post('/sections/education', data),
  updateEducation: (id: string, data: any) => apiClient.patch(`/sections/education/${id}`, data),
  deleteEducation: (id: string) => apiClient.delete(`/sections/education/${id}`),

  getCertificates: () => apiClient.get('/sections/certificates'),
  createCertificate: (data: any) => apiClient.post('/sections/certificates', data),
  updateCertificate: (id: string, data: any) => apiClient.patch(`/sections/certificates/${id}`, data),
  deleteCertificate: (id: string) => apiClient.delete(`/sections/certificates/${id}`),

  getExperience: () => apiClient.get('/sections/experience'),
  createExperience: (data: any) => apiClient.post('/sections/experience', data),
  updateExperience: (id: string, data: any) => apiClient.patch(`/sections/experience/${id}`, data),
  deleteExperience: (id: string) => apiClient.delete(`/sections/experience/${id}`),

  getSkills: () => apiClient.get('/sections/skills'),
  createSkillCategory: (data: any) => apiClient.post('/sections/skills', data),
  updateSkillCategory: (id: string, data: any) => apiClient.patch(`/sections/skills/${id}`, data),
  deleteSkillCategory: (id: string) => apiClient.delete(`/sections/skills/${id}`),

  getPortfolio: () => apiClient.get('/sections/portfolio'),
  createPortfolioProject: (data: any) => apiClient.post('/sections/portfolio', data),
  updatePortfolioProject: (id: string, data: any) => apiClient.patch(`/sections/portfolio/${id}`, data),
  deletePortfolioProject: (id: string) => apiClient.delete(`/sections/portfolio/${id}`),

  getFooter: () => apiClient.get('/sections/footer'),
  updateFooter: (id: string, data: any) => apiClient.patch(`/sections/footer/${id}`, data),
  createFooter: (data: any) => apiClient.post('/sections/footer', data),

  updateTheme: (data: any) => apiClient.patch('/theme', data),
  updateSiteConfig: (data: any) => apiClient.patch('/site-config', data),

  // File upload
  uploadSingle: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post('/upload/single', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadMultiple: (files: File[]) => {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    return apiClient.post('/upload/multiple', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  generateCV: (data: any) => apiClient.post('/ai/generate-cv', data),
  sendContact: (data: { name: string; email: string; subject: string; message: string }) =>
    apiClient.post('/contact', data),

  // Section visibility & order
  getSectionVisibility: () => apiClient.get('/users/me/section-visibility'),
  updateSectionVisibility: (data: Record<string, boolean>) =>
    apiClient.patch('/users/me/section-visibility', data),
  getSectionOrder: () => apiClient.get('/users/me/section-order'),
  updateSectionOrder: (order: string[]) =>
    apiClient.patch('/users/me/section-order', { order }),

  // ── Admin endpoints ──────────────────────────────────────────────────────
  admin: {
    getStats: () => apiClient.get('/users/admin/stats').then(r => r.data),
    getUsers: () => apiClient.get('/users').then(r => r.data),
    setActive: (id: string, isActive: boolean) =>
      apiClient.patch(`/users/${id}/active`, { isActive }).then(r => r.data),
    updateUser: (id: string, data: any) =>
      apiClient.patch(`/users/${id}`, data).then(r => r.data),
    deleteUser: (id: string) =>
      apiClient.delete(`/users/${id}`).then(r => r.data),
  },
};